import { Message } from '@patsnap/tahiti-core/common/message';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { HeaderServiceProxy } from '@telecom/shared/services/header-service/header-service-proxy';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import { TRACK_EVENT_TYPE } from '@/constants';
import {
    OnboardingBrowserRuntime,
    type OnboardingBrowserRuntime as OnboardingBrowserRuntimeType,
} from '../../common/services/onboarding-browser-runtime';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    OnboardingStateService,
    type OnboardingStateService as OnboardingStateServiceType,
} from '../../common/services/onboarding-state-service';
import { registerOnboardingLocaleMessages } from '../../common/utils/onboarding-locale';
import {
    FirstTaskRewardApi,
    type FirstTaskRewardAction,
    type FirstTaskRewardApi as FirstTaskRewardApiType,
    type FirstTaskRewardClaimRequest,
} from '../first-task-reward-api';
import {
    FirstTaskRewardPresenter,
    type FirstTaskRewardPresenter as FirstTaskRewardPresenterType,
    type FirstTaskRewardPromptHandle,
} from '../first-task-reward-presenter';
import type {
    FirstTaskRewardReportOptions,
    FirstTaskRewardService,
} from '../first-task-reward-service';
import { messages } from '../locales';

const TARGET_WAIT_TIMEOUT_MS = 5000;
const SHARE_ACTION_CONTEXT_TIMEOUT_MS = 60_000;
const EXPORT_ACTION_CONTEXT_TIMEOUT_MS = 16 * 60_000;
const PROMPT_VIEW_EVENT = 'ip_first_task_reward_prompt_view';
const PROMPT_CLOSE_EVENT = 'ip_first_task_reward_prompt_close';
const EXPORT_CLICK_EVENT = 'ip_first_task_reward_export_click';
const SHARE_CLICK_EVENT = 'ip_first_task_reward_share_click';

type PromptCloseReason = 'close' | 'export' | 'share';

type ActiveReportContext = Pick<FirstTaskRewardClaimRequest, 'task_id' | 'agent_type'>;

interface PendingAction {
    /** 弹窗内当前等待业务结果的动作。 */
    action: FirstTaskRewardAction;
    /** 激活上下文自动失效的定时器。 */
    timeoutId: number;
}

/** 编排三类报告的统一权益卡片、CTA 上下文及奖励领取。 */
@injectable()
export class FirstTaskRewardServiceImpl implements FirstTaskRewardService {
    private _lifecycleId = 0;
    private _activeReport?: ActiveReportContext;
    private _promptHandle?: FirstTaskRewardPromptHandle;
    private _pendingAction?: PendingAction;
    private _promptShown = false;

    // eslint-disable-next-line max-params
    constructor(
        @inject(OnboardingEligibilityService)
        private readonly _eligibilityService: OnboardingEligibilityServiceType,
        @inject(OnboardingStateService)
        private readonly _stateService: OnboardingStateServiceType,
        @inject(OnboardingBrowserRuntime)
        private readonly _runtime: OnboardingBrowserRuntimeType,
        @inject(FirstTaskRewardPresenter)
        private readonly _presenter: FirstTaskRewardPresenterType,
        @inject(FirstTaskRewardApi)
        private readonly _api: FirstTaskRewardApiType,
        @inject(HeaderServiceProxy)
        private readonly _headerServiceProxy: HeaderServiceProxy,
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        @inject(Message)
        private readonly _message: Message,
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
    ) {}

    startReport(options: FirstTaskRewardReportOptions): void {
        this.destroyReport();
        if (!options.taskId || !this._eligibilityService.isAudienceEligible()) return;

        this._activeReport = {
            task_id: options.taskId,
            agent_type: options.agentType,
        };
        const lifecycleId = this._lifecycleId;
        void this._startPrompt(options, lifecycleId);
    }

    destroyReport(): void {
        this._lifecycleId += 1;
        this._clearPendingAction();
        this._activeReport = undefined;
        this._promptHandle?.dispose();
        this._promptHandle = undefined;
        this._promptShown = false;
    }

    notifyActionSucceeded(action: FirstTaskRewardAction): void {
        const activeReport = this._activeReport;
        if (!activeReport) return;

        if (this._pendingAction?.action === action) {
            this._clearPendingAction();
            if (action === 'EXPORT') {
                this._closePrompt('export');
            }
        }
        const lifecycleId = this._lifecycleId;
        void this._api.claim({
            ...activeReport,
            action,
        }).then((response) => {
            if (response.status !== 'GRANTED' || !this._isCurrent(lifecycleId)) return;
            registerOnboardingLocaleMessages(this._i18nService, messages);
            const key = action === 'EXPORT'
                ? 'onboarding.firstTaskReward.toast.export'
                : 'onboarding.firstTaskReward.toast.share';
            this._message.success({
                message: this._i18nService.t(key),
            });
        }).catch(error => this._runtime.warn('Failed to claim first task reward:', error));
    }

    cancelPendingAction(action?: FirstTaskRewardAction): void {
        if (action && this._pendingAction?.action !== action) return;
        this._clearPendingAction();
    }

    // eslint-disable-next-line complexity
    private async _startPrompt(options: FirstTaskRewardReportOptions, lifecycleId: number): Promise<void> {
        try {
            const eligible = await this._eligibilityService.isReportEligible();
            if (!eligible || !this._isCurrent(lifecycleId)) return;

            const accountId = this._getAccountId();
            if (!accountId) return;

            await this._runtime.waitForRender();
            const [reportElement, shareElement, exportElement] = await Promise.all([
                this._runtime.waitForElement(options.reportSelector, TARGET_WAIT_TIMEOUT_MS),
                this._runtime.waitForElement(options.shareSelector, TARGET_WAIT_TIMEOUT_MS),
                this._runtime.waitForElement(options.exportSelector, TARGET_WAIT_TIMEOUT_MS),
            ]);
            if (!this._isCurrent(lifecycleId) || !reportElement || !shareElement || !exportElement) return;

            this._showPrompt(
                accountId,
                shareElement,
                exportElement,
                lifecycleId,
            );
        } catch (error) {
            if (this._isCurrent(lifecycleId)) {
                this._runtime.warn('Failed to start first task reward prompt:', error);
            }
        }
    }

    // eslint-disable-next-line max-params
    private _showPrompt(
        accountId: string,
        shareElement: Element,
        exportElement: Element,
        lifecycleId: number,
    ): void {
        if (this._promptShown || !this._isCurrent(lifecycleId)) return;

        this._promptShown = true;
        registerOnboardingLocaleMessages(this._i18nService, messages);
        try {
            this._promptHandle = this._presenter.showPrompt({
                title: this._i18nService.t('onboarding.firstTaskReward.prompt.title'),
                benefitPrefix: this._i18nService.t('onboarding.firstTaskReward.prompt.benefitPrefix'),
                credits: this._i18nService.t('onboarding.firstTaskReward.prompt.credits'),
                benefitSuffix: this._i18nService.t('onboarding.firstTaskReward.prompt.benefitSuffix'),
                exportNow: this._i18nService.t('onboarding.firstTaskReward.prompt.exportNow'),
                shareNow: this._i18nService.t('onboarding.firstTaskReward.prompt.shareNow'),
                validity: this._i18nService.t('onboarding.firstTaskReward.prompt.validity'),
                closeLabel: this._i18nService.t('onboarding.firstTaskReward.prompt.closeLabel'),
                onAction: action => this._handlePromptAction(action, shareElement, exportElement),
                onDismiss: () => this._closePrompt('close', false),
            });
        } catch (error) {
            this._promptShown = false;
            this._runtime.warn('Failed to mount first task reward prompt:', error);
            return;
        }

        this._track(PROMPT_VIEW_EVENT);
        void this._stateService.updateState(
            accountId,
            { first_task_reward_prompt_seen: true },
            'optimistic',
        ).catch(error => this._runtime.warn('Failed to mark first task reward prompt as seen:', error));
        if (!this._isCurrent(lifecycleId)) this._promptHandle?.dispose();
    }

    private _handlePromptAction(
        action: FirstTaskRewardAction,
        shareElement: Element,
        exportElement: Element,
    ): void {
        this._track(action === 'EXPORT' ? EXPORT_CLICK_EVENT : SHARE_CLICK_EVENT);
        this._clearPendingAction();
        const timeoutMs = action === 'EXPORT'
            ? EXPORT_ACTION_CONTEXT_TIMEOUT_MS
            : SHARE_ACTION_CONTEXT_TIMEOUT_MS;
        const timeoutId = this._runtime.setTimeout(() => this._clearPendingAction(), timeoutMs);
        this._pendingAction = {
            action,
            timeoutId,
        };
        if (action === 'EXPORT') {
            this._promptHandle?.setActionLoading('EXPORT');
        } else {
            this._closePrompt('share');
        }
        this._runtime.clickElement?.(action === 'EXPORT' ? exportElement : shareElement);
    }

    private _closePrompt(closeReason: PromptCloseReason, shouldDispose = true): void {
        const promptHandle = this._promptHandle;
        if (!promptHandle) return;
        this._promptHandle = undefined;
        if (shouldDispose) promptHandle.dispose();
        this._track(PROMPT_CLOSE_EVENT, { closeReason });
    }

    private _clearPendingAction(): void {
        if (!this._pendingAction) return;
        this._runtime.clearTimeout(this._pendingAction.timeoutId);
        this._pendingAction = undefined;
        this._promptHandle?.setActionLoading();
    }

    private _getAccountId(): string {
        return String(this._headerServiceProxy.headerService.account?._info.account_id ?? '');
    }

    private _isCurrent(lifecycleId: number): boolean {
        return lifecycleId === this._lifecycleId;
    }

    private _track(eventCode: string, detail?: Record<string, string>): void {
        void this._ravenService.track(eventCode, {
            sourceType: eventCode,
            eventType: TRACK_EVENT_TYPE.SS_PLG_PAGE_VIEW,
            ...(detail ? { eventDetailStr: detail } : {}),
        }).catch(() => undefined);
    }
}
